import React, { useEffect, useMemo, useState } from 'react';
import { PageFrame, PageHeader, Surface, FilterBar, FilterSelect, FilterMultiSelect, Button, TabBar, Note, loadingStats } from '@/components/ui';
import type { StatItem } from '@/components/ui';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { pageStyles } from '@/components/utils/PageLayout';
import './utils/cell-states.css';
import { Tooltip } from '@mui/material';
import {
  PortfolioLiquidityRiskItem,
  PortfolioLiquidityRiskResponse,
  ExposureRiskItem,
  ExposureRiskResponse,
  SelectOption
} from '@/types/client';
import { portfolioLiqRiskAPI, exposureRiskAPI, schemesApi } from '@/services/api';

const RiskCalculation: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'liquidity' | 'exposure'>('liquidity');

  // Schemes and filters state
  const [allSchemes, setAllSchemes] = useState<SelectOption[]>([]);
  const [schemes, setSchemes] = useState<SelectOption[]>([]);
  const [pendingSchemes, setPendingSchemes] = useState<SelectOption[]>([{ value: 'all', label: 'All' }]);
  const [appliedSchemes, setAppliedSchemes] = useState<SelectOption[]>([{ value: 'all', label: 'All' }]);
  const [loadingSchemes, setLoadingSchemes] = useState<boolean>(true);

  const [investmentStrategies, setInvestmentStrategies] = useState<SelectOption[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<SelectOption | null>(null);
  // The strategy in force for the loaded data — only moves on Apply, like appliedSchemes.
  const [appliedStrategy, setAppliedStrategy] = useState<SelectOption | null>(null);

  // Portfolio Liquidity Risk state
  const [liquidityData, setLiquidityData] = useState<PortfolioLiquidityRiskItem[]>([]);
  const [liquidityMeta, setLiquidityMeta] = useState<PortfolioLiquidityRiskResponse['meta'] | null>(null);
  const [liquidityLoading, setLiquidityLoading] = useState<boolean>(true); // first fetch runs once schemes load
  const [liquidityError, setLiquidityError] = useState<string | null>(null);

  // Exposure Risk state
  const [exposureData, setExposureData] = useState<ExposureRiskItem[]>([]);
  const [exposureMeta, setExposureMeta] = useState<ExposureRiskResponse['meta'] | null>(null);
  const [exposureLoading, setExposureLoading] = useState<boolean>(true); // first fetch runs once schemes load
  const [exposureError, setExposureError] = useState<string | null>(null);

  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  // Sorting state for Portfolio Liquidity Risk
  const [liquiditySortColumn, setLiquiditySortColumn] = useState<keyof PortfolioLiquidityRiskItem>('current_position');
  const [liquiditySortDirection, setLiquiditySortDirection] = useState<'asc' | 'desc'>('desc');

  // Sorting state for Exposure Risk
  const [exposureSortColumn, setExposureSortColumn] = useState<keyof ExposureRiskItem>('days');
  const [exposureSortDirection, setExposureSortDirection] = useState<'asc' | 'desc'>('asc');

  // Load schemes and investment strategies
  useEffect(() => {
    const loadSchemes = async () => {
      try {
        setLoadingSchemes(true);
        const defaultStrategyValue = 'Core Domestic';
        const schemesData = await schemesApi.getSchemes({ page_size: 1000, is_active: true });
        
        // Extract unique investment strategies
        const strategiesSet = new Set<string>();
        schemesData.results.forEach((scheme) => {
          if (scheme.investment_strategy_display || scheme.investment_strategy) {
            strategiesSet.add(scheme.investment_strategy_display || scheme.investment_strategy || '');
          }
        });
        
        const strategyOptions: SelectOption[] = Array.from(strategiesSet)
          .filter(s => s)
          .sort()
          .map((strategy) => ({ value: strategy, label: strategy }));
        
        setInvestmentStrategies(strategyOptions);

        // Store all schemes with their metadata
        const allSchemeOptions: SelectOption[] = [
          { value: 'all', label: 'All' },
          ...schemesData.results.map((scheme) => ({
            value: scheme.code || '',
            label: scheme.code || '',
            metadata: {
              investment_strategy: scheme.investment_strategy_display || scheme.investment_strategy
            }
          }))
        ];
        
        setAllSchemes(allSchemeOptions);
        setSchemes(allSchemeOptions);

        // Preselect default investment strategy if available
        const defaultStrategyOption = strategyOptions.find(
          (option) => option.value === defaultStrategyValue
        ) || null;

        if (defaultStrategyOption) {
          setSelectedStrategy(defaultStrategyOption);
          setAppliedStrategy(defaultStrategyOption);

          const filteredSchemes = allSchemeOptions.filter(
            (scheme) =>
              scheme.value !== 'all' &&
              scheme.metadata?.investment_strategy === defaultStrategyOption.value
          );

          setPendingSchemes(filteredSchemes);
          setAppliedSchemes(filteredSchemes);
        } else {
          setPendingSchemes([{ value: 'all', label: 'All' }]);
          setAppliedSchemes([{ value: 'all', label: 'All' }]);
        }
      } catch (err: any) {
        if (err?.code === 'ERR_NETWORK') {
          setLiquidityError('Connection with Server Failed!');
          setExposureError('Connection with Server Failed!');
        }
      } finally {
        setLoadingSchemes(false);
      }
    };

    loadSchemes();
  }, []);

  // Filter schemes based on selected investment strategy
  useEffect(() => {
    if (!selectedStrategy) {
      setSchemes(allSchemes);
      setPendingSchemes([{ value: 'all', label: 'All' }]);
    } else {
      const filteredSchemes = allSchemes.filter(
        (scheme) =>
          scheme.value !== 'all' &&
          scheme.metadata?.investment_strategy === selectedStrategy.value
      );
      setSchemes(filteredSchemes);
      setPendingSchemes(filteredSchemes);
    }
  }, [selectedStrategy, allSchemes]);

  const requestParams = useMemo(() => {
    const params: { scheme_codes?: string } = {};
    if (
      appliedSchemes.length > 0 &&
      !appliedSchemes.some((s) => s.value === 'all')
    ) {
      params.scheme_codes = appliedSchemes.map((s) => s.value).join(',');
    }
    return params;
  }, [appliedSchemes]);

  // Fetch liquidity data
  const fetchLiquidity = async () => {
    setLiquidityLoading(true);
    setLiquidityError(null);
    try {
      const response = await portfolioLiqRiskAPI.getPortfolioLiquidityRisk(requestParams);
      setLiquidityData(response.data || []);
      setLiquidityMeta(response.meta);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to load portfolio liquidity risk data';
      console.log(err);
      setLiquidityError(errorMessage);
    } finally {
      setLiquidityLoading(false);
    }
  };

  // Fetch exposure data
  const fetchExposure = async () => {
    setExposureLoading(true);
    setExposureError(null);
    try {
      const response = await exposureRiskAPI.getExposureRisk(requestParams);
      setExposureData(response.data || []);
      setExposureMeta(response.meta);
      setShowAlert(response.meta?.companies_with_no_median_value.length > 0);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Failed to load exposure risk data';
      console.log(err);
      setExposureError(errorMessage);
    } finally {
      setExposureLoading(false);
    }
  };

  // Fetch data for both APIs
  useEffect(() => {
    const fetchData = async () => {
      if (loadingSchemes) return;

      // Fetch both APIs in parallel
      await Promise.all([fetchLiquidity(), fetchExposure()]);
    };

    fetchData();
  }, [requestParams, loadingSchemes, refreshKey]);

  const handleLiquiditySort = (column: keyof PortfolioLiquidityRiskItem) => {
    if (liquiditySortColumn === column) {
      setLiquiditySortDirection(liquiditySortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLiquiditySortColumn(column);
      setLiquiditySortDirection('asc');
    }
  };

  const handleExposureSort = (column: keyof ExposureRiskItem) => {
    if (exposureSortColumn === column) {
      setExposureSortDirection(exposureSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setExposureSortColumn(column);
      setExposureSortDirection('asc');
    }
  };

  const getLiquiditySortIcon = (column: keyof PortfolioLiquidityRiskItem) => {
    if (liquiditySortColumn === column) return <span>{liquiditySortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const getExposureSortIcon = (column: keyof ExposureRiskItem) => {
    if (exposureSortColumn === column) return <span>{exposureSortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const formatNumber = (value: number, decimals = 2, multiple_of = 1): string => {
    value = value / multiple_of;
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const liquidityNumericColumns: (keyof PortfolioLiquidityRiskItem)[] = ['current_position', 'notional', 'max_permissible_position', 'median_tv_21d'];
  const exposureNumericColumns: (keyof ExposureRiskItem)[] = ['days', 'current_exposure_percent', 'minimum_exposure_percent'];

  const sortedLiquidityData = [...liquidityData].sort((a, b) => {
    // A newly listed company has no symbol yet; sort it as blank rather than null.
    let valA: any = a[liquiditySortColumn] ?? '';
    let valB: any = b[liquiditySortColumn] ?? '';

    if (liquidityNumericColumns.includes(liquiditySortColumn)) {
      valA = Number(valA);
      valB = Number(valB);
    } else if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = typeof valB === 'string' ? valB.toLowerCase() : valB;
    }

    if (valA < valB) return liquiditySortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return liquiditySortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const sortedExposureData = [...exposureData].sort((a, b) => {
    let valA: any = a[exposureSortColumn];
    let valB: any = b[exposureSortColumn];

    if (exposureNumericColumns.includes(exposureSortColumn)) {
      valA = Number(valA);
      valB = Number(valB);
    } else if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = typeof valB === 'string' ? valB.toLowerCase() : valB;
    }

    if (valA < valB) return exposureSortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return exposureSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSchemeChange = (_event: any, value: SelectOption[]) => {
    if (!value || value.length === 0) {
      setPendingSchemes([]);
      return;
    }

    const hasAll = value.some(option => option.value === 'all');
    const lastSelected = value[value.length - 1];

    if (lastSelected.value === 'all') {
      setPendingSchemes([{ value: 'all', label: 'All' }]);
    } else if (hasAll && value.length > 1) {
      setPendingSchemes(value.filter(option => option.value !== 'all'));
    } else {
      setPendingSchemes(value);
    }
  };

  const handleStrategyChange = (_event: any, value: SelectOption | null) => {
    setSelectedStrategy(value);
  };

  const arraysEqualByValue = (a: SelectOption[], b: SelectOption[]) => {
    if (a.length !== b.length) return false;
    const aVals = a.map(x => x.value).sort();
    const bVals = b.map(x => x.value).sort();
    for (let i = 0; i < aVals.length; i++) {
      if (aVals[i] !== bVals[i]) return false;
    }
    return true;
  };

  const applyFilters = () => {
    setAppliedStrategy(selectedStrategy);
    if (!arraysEqualByValue(pendingSchemes, appliedSchemes)) {
      setAppliedSchemes(pendingSchemes);
      setRefreshKey((prev) => prev + 1);
    }
  };

  const isLoading = liquidityLoading || exposureLoading;

  /** What the loaded figures cover — the applied selection, not the pending one. */
  const allSchemesApplied = appliedSchemes.length === 0 || appliedSchemes.some(s => s.value === 'all');
  const appliedSchemeCodes = appliedSchemes.map(s => s.label);
  const selectionStats: StatItem[] = [
    {
      label: 'Strategy',
      value: appliedStrategy?.label ?? 'All',
      title: 'Investment strategy applied to the figures below',
    },
    {
      label: 'Schemes',
      value: allSchemesApplied ? 'All' : appliedSchemes.length,
      unit: allSchemesApplied ? undefined : appliedSchemes.length === 1 ? 'scheme' : 'schemes',
      hint: allSchemesApplied ? undefined : appliedSchemeCodes.join(', '),
      wrapHint: true,
      title: allSchemesApplied ? 'All schemes' : appliedSchemeCodes.join(', '),
    },
  ];
  
  // Use exposure meta for display (as requested)
  const displayMeta = exposureMeta;

  /* Both tabs are standard tables. Cell tone comes from the shared cell-state
     classes via `cellClassName`, so a breach or a missing median reads the same
     here as anywhere else in the app. */
  const liquidityColumns = useMemo<ColumnDef<PortfolioLiquidityRiskItem>[]>(() => {
    const positionTone = (row: PortfolioLiquidityRiskItem) =>
      !row.is_median_value_available
        ? ''
        : row.current_position > row.max_permissible_position
          ? 'cell-negative'
          : 'cell-positive';
    const medianTone = (row: PortfolioLiquidityRiskItem) =>
      row.is_median_value_available ? '' : 'cell-failure';
    const caution = (row: PortfolioLiquidityRiskItem) =>
      row.is_median_value_available ? null : (
        <Tooltip title="No median value found">
          <span aria-label="caution" style={{ marginRight: 6 }}>⚠️</span>
        </Tooltip>
      );

    return [
      { key: 'company_symbol', header: 'Company', sortable: true, width: '180px',
        tooltip: 'Company symbol', render: r => r.company_symbol ?? '—' },
      { key: 'current_position', header: 'Current Position (%)', sortable: true, align: 'right', width: '190px',
        tooltip: 'Current position, as a percentage of the portfolio',
        cellClassName: positionTone,
        render: r => formatNumber(r.current_position, 1) },
      { key: 'notional', header: 'Notional (₹ Cr)', sortable: true, align: 'right', width: '170px',
        tooltip: 'Notional value',
        cellClassName: positionTone,
        render: r => formatNumber(r.notional, 2, 10000000) },
      { key: 'max_permissible_position', header: 'Max Permissible Pos. (%)', sortable: true, align: 'right', width: '220px',
        tooltip: 'Maximum permissible position under the liquidity limits',
        cellClassName: medianTone,
        render: r => <>{caution(r)}{formatNumber(r.max_permissible_position, 1)}</> },
      { key: 'median_tv_21d', header: 'Median Value (₹ Cr)', sortable: true, align: 'right', width: '190px',
        tooltip: 'Median traded value over the last 21 days',
        cellClassName: medianTone,
        render: r => <>{caution(r)}{formatNumber(r.median_tv_21d, 2, 10000000)}</> },
    ];
  }, []);

  const exposureColumns = useMemo<ColumnDef<ExposureRiskItem>[]>(() => {
    const tone = (row: ExposureRiskItem) =>
      row.current_exposure_percent >= row.minimum_exposure_percent ? 'cell-positive' : 'cell-negative';
    return [
      { key: 'days', header: 'Days', sortable: true, align: 'right', width: '140px',
        tooltip: 'Number of days', render: r => r.days },
      { key: 'current_exposure_percent', header: 'Current Exposure (%)', sortable: true, align: 'right', width: '200px',
        tooltip: 'Current exposure percentage',
        cellClassName: tone,
        render: r => formatNumber(r.current_exposure_percent, 2) },
      { key: 'minimum_exposure_percent', header: 'Minimum Exposure (%)', sortable: true, align: 'right', width: '210px',
        tooltip: 'Minimum exposure percentage',
        cellClassName: tone,
        render: r => formatNumber(r.minimum_exposure_percent, 2) },
    ];
  }, []);

  return (
    <PageFrame
      header={
        <PageHeader
          title="Risk Calculation"
          subtitle="Exposure and liquidity risk against the portfolio's limits."
          // All four tiles arrive together with the first load, so the row never
          // grows in two steps.
          stats={
            exposureLoading
              ? loadingStats(['Strategy', 'Schemes', 'Companies', 'AUM'])
              : displayMeta ? [
              ...selectionStats,
              { label: 'Companies', value: displayMeta.total_companies, title: 'Total Companies' },
              {
                label: 'AUM',
                value: `₹${formatNumber(displayMeta.aum, 2, 10000000)}`,
                unit: 'Cr',
                title: 'Assets Under Management',
              },
            ] : undefined
          }
        />
      }
    >
      {/* Companies the exposure model had no median value for */}
      {showAlert && exposureMeta && exposureMeta.companies_with_no_median_value.length > 0 && (
        <Note tone="warning" onDismiss={() => setShowAlert(false)}>
          No median value available for: {JSON.stringify(exposureMeta.companies_with_no_median_value)}
        </Note>
      )}

      <FilterBar
        hideSearch
        filters={
          <>

          <FilterSelect
            label="Investment Strategy"
            options={investmentStrategies}
            value={selectedStrategy}
            onChange={value => handleStrategyChange(null, value)}
          />
          <div style={{ gridColumn: '1 / -1' }}>
            <FilterMultiSelect
              label="Schemes"
              limitTags={1}
              options={schemes}
              value={pendingSchemes}
              onChange={next => handleSchemeChange(null, next)}
              loading={loadingSchemes}
            />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip
              title={
                isLoading
                  ? 'Please wait…'
                  : arraysEqualByValue(pendingSchemes, appliedSchemes)
                    ? 'No changes to apply'
                    : 'Apply selected filters'
              }
            >
              <span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={applyFilters}
                  disabled={isLoading || arraysEqualByValue(pendingSchemes, appliedSchemes)}
                >
                  {isLoading ? 'Applying…' : 'Apply'}
                </Button>
              </span>
            </Tooltip>
          </div>
          </>
        }
      />

      <Surface>


        {/* Tabs */}
        <div className={pageStyles['card-tabs']}>
          <TabBar
            ariaLabel="Risk view"
            value={activeTab}
            onChange={v => setActiveTab(v as 'liquidity' | 'exposure')}
            tabs={[
              { value: 'liquidity', label: 'Portfolio Liquidity Risk' },
              { value: 'exposure', label: 'Overall Exposure Risk' },
            ]}
          />
        </div>

        {/* Portfolio Liquidity Risk Tab */}
        {activeTab === 'liquidity' && (
          <DataTable<PortfolioLiquidityRiskItem>
            embedded
            data={sortedLiquidityData}
            columns={liquidityColumns}
            rowKey={row => String(row.company_id)}
            totalCount={sortedLiquidityData.length}
            loading={liquidityLoading}
            error={liquidityError}
            currentPage={1}
            totalPages={1}
            onPageChange={() => undefined}
            pageSize={sortedLiquidityData.length || 1}
            sortField={liquiditySortColumn}
            sortDir={liquiditySortDirection}
            onSort={field => handleLiquiditySort(field as keyof PortfolioLiquidityRiskItem)}
            hideSearch
            loadingMessage="Loading liquidity risk…"
            emptyMessage="No liquidity risk data for the selected schemes."
          />
        )}

        {activeTab === 'exposure' && (
          <DataTable<ExposureRiskItem>
            embedded
            data={sortedExposureData}
            columns={exposureColumns}
            rowKey={(row, i) => `${row.days}-${i}`}
            totalCount={sortedExposureData.length}
            loading={exposureLoading}
            error={exposureError}
            currentPage={1}
            totalPages={1}
            onPageChange={() => undefined}
            pageSize={sortedExposureData.length || 1}
            sortField={exposureSortColumn}
            sortDir={exposureSortDirection}
            onSort={field => handleExposureSort(field as keyof ExposureRiskItem)}
            hideSearch
            loadingMessage="Loading exposure risk…"
            emptyMessage="No exposure risk data for the selected schemes."
          />
        )}
      </Surface>
    </PageFrame>
  );
};

export default RiskCalculation;